import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Lightbulb, HeartHandshake, Coins, UserCircle } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function MechanismSection() {
  return <section className="py-14 px-4 bg-white relative overflow-hidden" data-id="element-79">
      {/* Decorative background element */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gold-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 opacity-50 pointer-events-none" data-id="element-80" />

      <div className="max-w-4xl mx-auto space-y-14 relative z-10" data-id="element-81">
        {/* La Promesa */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="text-center space-y-8" data-id="element-82">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-navy-900" data-id="element-83">
            Ahora imagina lo contrario.
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mt-12 text-left" data-id="element-84">
            <div className="bg-navy-50 p-8 rounded-2xl border border-navy-100 transition-transform hover:-translate-y-1 duration-300" data-id="element-85">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm mb-6 text-gold-600" data-id="element-86">
                <HeartHandshake className="w-6 h-6" data-id="element-87" />
              </div>
              <p className="text-navy-700 leading-relaxed" data-id="element-88">
                Imagina levantarte con una claridad que no tenías antes. Saber
                exactamente qué estaba bloqueando tu relación con Dios — y
                haberlo soltado.
              </p>
            </div>

            <div className="bg-navy-50 p-8 rounded-2xl border border-navy-100 transition-transform hover:-translate-y-1 duration-300" data-id="element-89">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm mb-6 text-gold-600" data-id="element-90">
                <Coins className="w-6 h-6" data-id="element-91" />
              </div>
              <p className="text-navy-700 leading-relaxed" data-id="element-92">
                Imagina tener una relación con el dinero que no te genere culpa,
                miedo ni vergüenza. Sino paz. Estrategia. Abundancia con
                propósito.
              </p>
            </div>

            <div className="bg-navy-50 p-8 rounded-2xl border border-navy-100 transition-transform hover:-translate-y-1 duration-300" data-id="element-93">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm mb-6 text-gold-600" data-id="element-94">
                <UserCircle className="w-6 h-6" data-id="element-95" />
              </div>
              <p className="text-navy-700 leading-relaxed" data-id="element-96">
                Imagina tener una disciplina que no dependa de motivación, sino
                de identidad. De saber quién eres y para qué fuiste creado.
              </p>
            </div>
          </div>

          <p className="text-2xl font-serif text-navy-900 mt-12 italic" data-id="element-97">
            Eso no es un sueño lejano. Es el resultado natural de sanar lo que
            está roto.
          </p>
        </motion.div>

        {/* El Mecanismo */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="space-y-12" data-id="element-98">
          <div className="text-center" data-id="element-99">
            <div className="inline-flex items-center gap-2 text-navy-600 mb-6" data-id="element-100">
              <Lightbulb className="w-6 h-6" data-id="element-101" />
              <h2 className="text-sm font-bold tracking-widest uppercase" data-id="element-102">
                El Mecanismo
              </h2>
            </div>
            <h3 className="text-4xl font-serif font-bold text-navy-900 mb-6" data-id="element-103">
              El Triángulo Roto
            </h3>
            <p className="text-xl text-navy-600 max-w-2xl mx-auto" data-id="element-104">
              Son tres heridas que casi nadie conecta — pero que están
              profundamente entrelazadas:
            </p>
          </div>

          <div className="overflow-x-auto pb-4" data-id="element-105">
            <table className="w-full text-left border-collapse min-w-[600px]" data-id="element-106">
              <thead data-id="element-107">
                <tr className="border-b-2 border-navy-900" data-id="element-108">
                  <th className="py-4 px-6 font-serif text-xl text-navy-900 w-1/3" data-id="element-109">
                    Herida
                  </th>
                  <th className="py-4 px-6 font-serif text-xl text-navy-900 w-1/3" data-id="element-110">
                    Qué Destruye
                  </th>
                  <th className="py-4 px-6 font-serif text-xl text-navy-900 w-1/3" data-id="element-111">
                    Síntoma Visible
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100" data-id="element-112">
                <tr className="hover:bg-navy-50 transition-colors" data-id="element-113">
                  <td className="py-6 px-6" data-id="element-114">
                    <div className="flex items-center gap-3 font-semibold text-navy-900 text-lg" data-id="element-115">
                      <span className="text-2xl" data-id="element-116">🙏</span> Herida Espiritual
                    </div>
                  </td>
                  <td className="py-6 px-6 text-navy-700" data-id="element-117">
                    Tu relación con Dios y tu sentido de valor
                  </td>
                  <td className="py-6 px-6 text-navy-700" data-id="element-118">
                    Orar sin convicción, sentirte indigno, vivir con culpa
                    crónica
                  </td>
                </tr>
                <tr className="hover:bg-navy-50 transition-colors" data-id="element-119">
                  <td className="py-6 px-6" data-id="element-120">
                    <div className="flex items-center gap-3 font-semibold text-navy-900 text-lg" data-id="element-121">
                      <span className="text-2xl" data-id="element-122">💰</span> Herida Financiera
                    </div>
                  </td>
                  <td className="py-6 px-6 text-navy-700" data-id="element-123">
                    Tu relación con el dinero y la abundancia
                  </td>
                  <td className="py-6 px-6 text-navy-700" data-id="element-124">
                    Autosabotaje, deudas cíclicas, miedo a pedir o cobrar
                  </td>
                </tr>
                <tr className="hover:bg-navy-50 transition-colors" data-id="element-125">
                  <td className="py-6 px-6" data-id="element-126">
                    <div className="flex items-center gap-3 font-semibold text-navy-900 text-lg" data-id="element-127">
                      <span className="text-2xl" data-id="element-128">🔥</span> Herida de Identidad
                    </div>
                  </td>
                  <td className="py-6 px-6 text-navy-700" data-id="element-129">
                    Tu disciplina, tus hábitos y tu propósito
                  </td>
                  <td className="py-6 px-6 text-navy-700" data-id="element-130">
                    Procrastinación, rendirse fácil, no creer en uno mismo
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-navy-900 text-white p-8 rounded-2xl text-center max-w-3xl mx-auto shadow-xl" data-id="element-131">
            <p className="text-lg text-navy-200 mb-2" data-id="element-132">
              Cuando una de las tres está herida, contamina las otras dos.
            </p>
            <p className="text-2xl font-serif text-gold-400" data-id="element-133">
              Pero cuando sanas las tres en conversación directa, personalizada
              y honesta...
            </p>
            <p className="text-xl font-bold mt-4" data-id="element-134">
              El ciclo se rompe. Por primera vez. De verdad.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}